import { friendlyTokenLabel } from './mergeFieldCatalog';

/**
 * Pure, deterministic placeholder-redaction segmenter for the PII-safe "Sent"
 * message preview (PRO-0620 follow-up). Splits an AUTHORED, send-level body (merge
 * tokens STILL UNRESOLVED — e.g. `{{child_name}}`) into an ordered run of literal
 * text and neutral token placeholders. It NEVER resolves a token to a value and
 * NEVER reads per-recipient content — it only names the placeholder via
 * `friendlyTokenLabel` so the UI can render a chip like "[Child's name]".
 *
 * The token grammar is the SAME single-pass, non-recursive `{{ identifier }}`
 * grammar as `validateTemplateContent.scanTokens` / `resolveMergeFields.nextToken`
 * (allow-list AND out-of-vocabulary well-formed tokens both become chips;
 * malformed / nested / unterminated `{{...}}` and single-brace groups stay
 * LITERAL text). This is presentation redaction only — deliberately NOT automated
 * NER/pattern scrubbing of free text (SEC-3: token redaction only, no false
 * confidence over arbitrary authored prose).
 */

/** The SAME valid-token identifier grammar as the validation/resolution engines. */
const IDENTIFIER = /^[a-z_][a-z0-9_]*$/i;

export type AuthoredPreviewSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'token'; readonly token: string; readonly label: string };

interface TokenMatch {
  readonly open: number;
  readonly end: number;
  /** Trimmed inner identifier when this is a well-formed `{{ identifier }}`. */
  readonly name: string | null;
}

/** Find the next `{{ ... }}`; `name === null` for malformed/nested/unterminated. */
function nextToken(text: string, from: number): TokenMatch | null {
  const open = text.indexOf('{{', from);
  if (open === -1) {
    return null;
  }
  const close = text.indexOf('}}', open + 2);
  if (close === -1) {
    return { open, end: text.length, name: null };
  }
  const inner = text.slice(open + 2, close);
  if (inner.includes('{') || inner.includes('}')) {
    return { open, end: close + 2, name: null };
  }
  const name = inner.trim();
  if (name === '' || !IDENTIFIER.test(name)) {
    return { open, end: close + 2, name: null };
  }
  return { open, end: close + 2, name };
}

/**
 * Segment an authored body into literal text + placeholder tokens. Adjacent
 * literal text (including the raw slice for a malformed `{{...}}`) is merged into
 * a single `text` segment, so the output alternates text/token cleanly.
 */
export function segmentAuthoredBody(body: string): AuthoredPreviewSegment[] {
  const segments: AuthoredPreviewSegment[] = [];
  let cursor = 0;
  let pendingText = '';

  const flushText = (): void => {
    if (pendingText.length > 0) {
      segments.push({ kind: 'text', text: pendingText });
      pendingText = '';
    }
  };

  while (cursor < body.length) {
    const match = nextToken(body, cursor);
    if (!match) {
      pendingText += body.slice(cursor);
      break;
    }
    pendingText += body.slice(cursor, match.open);
    if (match.name === null) {
      // Malformed/nested/unterminated — keep the raw slice as literal text.
      pendingText += body.slice(match.open, match.end);
    } else {
      flushText();
      segments.push({ kind: 'token', token: match.name, label: friendlyTokenLabel(match.name) });
    }
    cursor = match.end;
  }

  flushText();
  return segments;
}
